import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  FiFileText,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
} from 'react-icons/fi';

export type ApplicationStatus =
  | 'draft'
  | 'pending'
  | 'submitted'
  | 'verification'
  | 'in_review'
  | 'scored'
  | 'approved'
  | 'rejected';

interface StatusBadgeProps {
  status: ApplicationStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const Badge = styled.div<{ $status: ApplicationStatus; $size: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => (props.$size === 'sm' ? theme.spacing.xs : theme.spacing.sm)};
  padding: ${props => {
    switch (props.$size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.sm}`;
      case 'lg':
        return `${theme.spacing.sm} ${theme.spacing.lg}`;
      default:
        return `${theme.spacing.xs} ${theme.spacing.md}`;
    }
  }};
  border-radius: ${theme.borderRadius.full};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm':
        return theme.typography.sizes.xs;
      case 'lg':
        return theme.typography.sizes.base;
      default:
        return theme.typography.sizes.sm;
    }
  }};
  font-weight: ${theme.typography.weights.semibold};
  background: ${props => {
    switch (props.$status) {
      case 'approved':
        return theme.colors.success + '20';
      case 'rejected':
        return theme.colors.error + '20';
      case 'scored':
        return theme.colors.info + '20';
      case 'pending':
      case 'submitted':
      case 'verification':
        return theme.colors.warning + '20';
      case 'in_review':
        return theme.colors.primary + '20';
      default:
        return theme.colors.surface;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'approved':
        return theme.colors.success;
      case 'rejected':
        return theme.colors.error;
      case 'scored':
        return theme.colors.info;
      case 'pending':
      case 'submitted':
      case 'verification':
        return theme.colors.warning;
      case 'in_review':
        return theme.colors.primary;
      default:
        return theme.colors.text + '60';
    }
  }};

  svg {
    font-size: ${props => (props.$size === 'sm' ? '14px' : props.$size === 'lg' ? '20px' : '16px')};
  }
`;

const statusConfig: Record<
  ApplicationStatus,
  { label: string; icon: React.ReactNode }
> = {
  draft: { label: 'Brouillon', icon: <FiFileText /> },
  pending: { label: 'En attente', icon: <FiClock /> },
  submitted: { label: 'Soumise', icon: <FiClock /> },
  verification: { label: 'En vérification', icon: <FiAlertCircle /> },
  in_review: { label: 'En révision', icon: <FiAlertCircle /> },
  scored: { label: 'Scorée', icon: <FiCheckCircle /> },
  approved: { label: 'Approuvée', icon: <FiCheckCircle /> },
  rejected: { label: 'Rejetée', icon: <FiXCircle /> },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  showIcon = true,
}) => {
  const config = statusConfig[status];

  return (
    <Badge $status={status} $size={size}>
      {showIcon && config.icon}
      {config.label}
    </Badge>
  );
};
